import type { Metadata } from "next";
import { getTranslations } from "next-intl/server";

import { localePath, routing, type Locale } from "@/i18n/routing";
import { isNoindexLanding } from "@/lib/landingPages";

export const SITE_URL = "https://briefpanel.com";

/**
 * Build an absolute, locale-prefixed URL for a path on the marketing site.
 * The default locale has no prefix (matches the `as-needed` routing strategy).
 */
export function buildLocalizedUrl(locale: Locale, path: string): string {
  return `${SITE_URL}${localePath(locale, path)}`;
}

/** The size of the share card each blog post's `opengraph-image` route draws. */
export const BLOG_CARD_SIZE = { width: 1200, height: 630 };

/**
 * Where a post's share card is published. Next's own tag for the route keeps
 * the `[locale]` segment, so English cards came out as `/en-US/...`, a 307.
 */
export function buildBlogCardUrl(locale: Locale, slug: string): string {
  return buildLocalizedUrl(locale, `/blog/${slug}/opengraph-image`);
}

/**
 * Map an internal routing locale id to the hreflang code published in markup.
 * The routing id is `en-US`, but the rest of the site (locale layout, landing
 * pages) emits the generic `en` hreflang — keep the blog/sitemap consistent.
 */
const LOCALE_TO_HREFLANG: Record<string, string> = {
  "en-US": "en",
};

/**
 * hreflang map for a path across its locales, every locale by default. Shared
 * by `sitemap.ts` and per-page `generateMetadata` so the alternate URLs always
 * stay in sync.
 *
 * `x-default` points at the default locale, so it is declared only when that
 * locale is one of the page's: a page that exists in Portuguese alone must not
 * name an English URL that 404s (the radar hubs, #1447).
 */
export function buildHreflangLanguages(
  path: string,
  locales: readonly Locale[] = routing.locales,
): Record<string, string> {
  const languages: Record<string, string> = {};

  for (const locale of locales) {
    languages[toHreflang(locale)] = buildLocalizedUrl(locale, path);
  }

  if (locales.includes(routing.defaultLocale)) {
    languages["x-default"] = buildLocalizedUrl(routing.defaultLocale, path);
  }

  return languages;
}

/**
 * `alternates` metadata for a localized page: a self-referencing canonical
 * plus the hreflang set of the locales it exists in (every locale unless
 * given). Use in `generateMetadata` so each page declares its own canonical
 * instead of inheriting the homepage one from the layout.
 */
export function buildPageAlternates(
  locale: Locale,
  path: string,
  locales?: readonly Locale[],
) {
  return {
    canonical: buildLocalizedUrl(locale, path),
    languages: buildHreflangLanguages(path, locales),
  };
}

/** Props every localized page under `app/[locale]` receives. */
export type LocalePageProps = {
  params: Promise<{ locale: string }>;
};

/**
 * `generateMetadata` for a page whose title and description live under
 * `<namespace>.metadata` in the message files.
 *
 * A page without its own `generateMetadata` inherits the homepage title,
 * description and hreflang from the locale layout — which puts the home's
 * search-target phrase on every other indexable URL. Assign this to the
 * page's `generateMetadata` export instead of restating the same object.
 */
export function localizedPageMetadata(namespace: string, path: string) {
  return async ({ params }: LocalePageProps): Promise<Metadata> => {
    const { locale } = await params;
    const resolvedLocale = locale as Locale;
    const t = await getTranslations({ locale, namespace });
    const title = t("metadata.title");
    const description = t("metadata.description");

    return {
      title,
      description,
      robots: isNoindexLanding(path)
        ? { index: false, follow: false }
        : undefined,
      alternates: buildPageAlternates(resolvedLocale, path),
      openGraph: {
        title,
        description,
        url: buildLocalizedUrl(resolvedLocale, path),
        type: "website",
      },
    };
  };
}

type BlogPostingInput = {
  slug: string;
  /** The page's path; `/blog/<slug>` when absent. */
  path?: string;
  locale: Locale;
  title: string;
  description: string;
  publishedAt?: string;
  updatedAt?: string;
  image?: string;
};

/**
 * schema.org `BlogPosting` JSON-LD for an individual post. Rendered into a
 * `<script type="application/ld+json">` so search engines get explicit
 * article metadata (headline, dates, author/publisher) on top of the OG tags.
 */
export function buildBlogPostingJsonLd(post: BlogPostingInput) {
  const url = buildLocalizedUrl(post.locale, post.path ?? `/blog/${post.slug}`);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: toHreflang(post.locale),
    ...(post.publishedAt
      ? {
          datePublished: post.publishedAt,
          dateModified: post.updatedAt ?? post.publishedAt,
        }
      : {}),
    ...(post.image ? { image: [toAbsoluteUrl(post.image)] } : {}),
    author: { "@type": "Organization", name: "BriefPanel", url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "BriefPanel",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo-name.png`,
      },
    },
  };
}

type CollectionPageInput = {
  locale: Locale;
  path: string;
  title: string;
  description: string;
  /** The pages the collection lists, in order. */
  items: ReadonlyArray<{ name: string; url: string }>;
  /** Trail above the page, without the page itself or the site root. */
  breadcrumb: ReadonlyArray<{ name: string; path: string }>;
};

/**
 * schema.org JSON-LD for a page that lists other pages (the radar hubs,
 * #1447): a `CollectionPage` whose main entity is an `ItemList` of what it
 * lists, and a `BreadcrumbList` from the site root to the page. Deliberately
 * no `JobPosting` or other per-item type: Google reserves those for the page
 * of the item itself, with the data visible on it.
 */
export function buildCollectionPageJsonLd(page: CollectionPageInput) {
  const url = buildLocalizedUrl(page.locale, page.path);

  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: page.title,
      description: page.description,
      url,
      inLanguage: toHreflang(page.locale),
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: page.items.length,
        itemListElement: page.items.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          url: item.url,
        })),
      },
    },
    buildBreadcrumbJsonLd(page.locale, [
      ...page.breadcrumb,
      { name: page.title, path: page.path },
    ]),
  ];
}

export type FaqEntry = { question: string; answer: string };

/** schema.org `FAQPage`: the questions a page shows, with their answers. */
export function buildFaqPageJsonLd(faq: ReadonlyArray<FaqEntry>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: { "@type": "Answer", text: entry.answer },
    })),
  };
}

/**
 * schema.org `BreadcrumbList` from the site root down to the page: `trail` is
 * every crumb below the root, the page itself last.
 */
export function buildBreadcrumbJsonLd(
  locale: Locale,
  trail: ReadonlyArray<{ name: string; path: string }>,
) {
  const crumbs = [{ name: "BriefPanel", path: "/" }, ...trail];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: buildLocalizedUrl(locale, crumb.path),
    })),
  };
}

/**
 * JSON-LD serialized for an inline `<script>`. `<` is escaped so text read off
 * a third-party page (an item's title) cannot close the script tag.
 */
export function serializeJsonLd(jsonLd: unknown): string {
  return JSON.stringify(jsonLd).replace(/</g, "\\u003c");
}

/**
 * Metadata for a landing page: title and description from the page's own i18n
 * namespace, canonical + hreflang alternates, and the matching OpenGraph card.
 *
 * Every landing built this object with the same fifteen lines, differing only
 * in its namespace and slug (#1772). Whether the page is indexable comes from
 * lib/landingPages.ts, which also decides its sitemap and grid listing (#2825):
 * a `noindex` page still renders, it is just not indexed.
 */
export async function buildLandingMetadata({
  locale,
  namespace,
  slug,
}: {
  locale: string;
  namespace: string;
  slug: string;
}): Promise<Metadata> {
  const t = await getTranslations({ locale, namespace });
  const resolvedLocale = locale as Locale;
  const title = t("metadata.title");
  const description = t("metadata.description");

  return {
    title,
    description,
    robots: isNoindexLanding(slug)
      ? { index: false, follow: false }
      : undefined,
    alternates: buildPageAlternates(resolvedLocale, slug),
    openGraph: {
      title,
      description,
      url: buildLocalizedUrl(resolvedLocale, slug),
    },
  };
}

// --- private helpers ------------------------------------------------------
// Below every export, as `local/exported-functions-first` requires: the public
// surface of this module reads top to bottom without stepping over internals.

function toHreflang(locale: string): string {
  return LOCALE_TO_HREFLANG[locale] ?? locale;
}

function toAbsoluteUrl(src: string): string {
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  return `${SITE_URL}${src.startsWith("/") ? src : `/${src}`}`;
}
